import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { type HiringReliabilityConfig } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useToast } from "@src/appV2/lib";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import pluralize from "pluralize";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  PLACEMENT_CANDIDATE_BAN_ACTION,
  type PlacementCandidateBanAction,
} from "../../PlacementCandidate/types";
import { useReportInterviewLateness } from "../api/useReportInterviewLateness";

interface InterviewLateRescheduleBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  interviewId: string;
  hiringReliability: HiringReliabilityConfig;
  isReschedulingPastInterview: boolean;
  isReschedulingForSameDay: boolean;
  bansCountByAction: Record<PlacementCandidateBanAction, number> | undefined;
  onProceedWithReschedule: () => Promise<void>;
}

export function InterviewLateRescheduleBottomSheet(props: InterviewLateRescheduleBottomSheetProps) {
  const {
    isProspectWorkplace,
    modalState,
    interviewId,
    hiringReliability,
    isReschedulingPastInterview,
    isReschedulingForSameDay,
    bansCountByAction,
    onProceedWithReschedule,
  } = props;

  const { showErrorToast, showSuccessToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const rescheduleLabel = isProspectWorkplace ? "walk-in" : "interview";

  const { mutateAsync: reportLateness, isLoading: isReportingLateness } =
    useReportInterviewLateness({
      onSuccess: () => {
        showSuccessToast(
          "Thanks for letting us know! We gave the facility a heads up. They're looking forward to seeing you soon!"
        );
        modalState.closeModal();
      },
      onError: () => {
        showErrorToast(`An error occurred reporting lateness for ${rescheduleLabel}`);
      },
    });

  const handleReportLateness = async () => {
    await reportLateness({
      interviewId,
    });
  };

  const handleProceedWithReschedule = async () => {
    try {
      setIsLoading(true);
      await onProceedWithReschedule();
      modalState.closeModal();
    } catch {
      showErrorToast(`An error occurred rescheduling ${rescheduleLabel}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    modalState.closeModal();
  };

  const getNewBansCountAndPenalty = () => {
    const { penaltyMappers } = hiringReliability;
    const action = isReschedulingPastInterview
      ? PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_NCNS
      : PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_RESCHEDULED;

    const bansCount = bansCountByAction?.[action] ?? 0;
    const newBansCount = bansCount + 1;
    const penalty = penaltyMappers[action][String(newBansCount)];
    return { newBansCount, penalty };
  };

  const getDescription = () => {
    const { maxLateCancellationHours, penaltyMappers, penaltyLabelMapper, bansCountLabelMapper } =
      hiringReliability;

    const allowedLateCancellations = Object.values(
      penaltyMappers[PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_RESCHEDULED]
    ).filter((value) => value === 0).length;

    const { newBansCount, penalty } = getNewBansCountAndPenalty();
    const penaltyLabel = penaltyLabelMapper[penalty] ?? `${penalty} ${pluralize("day", penalty)}`;
    const bansCountLabel = bansCountLabelMapper[newBansCount] ?? newBansCount;

    if (isReschedulingPastInterview) {
      return `You're trying to reschedule after your interview has already started. If you move it to another day, it will count as a No-Show. Since this is your ${bansCountLabel}, continuing will restrict you from viewing job openings for ${penaltyLabel}.`;
    }

    const baseStatement = `You're rescheduling less than ${maxLateCancellationHours} hours before your interview. This is your ${bansCountLabel} late reschedule`;

    if (newBansCount <= allowedLateCancellations) {
      const pluralizedLateCancellations = pluralize("late reschedule", allowedLateCancellations);
      return `${baseStatement}. You're allowed up to ${allowedLateCancellations} ${pluralizedLateCancellations}. Any further ones will result in a restriction from viewing job openings.`;
    }

    return `${baseStatement}. If you continue, you'll be restricted from viewing job openings for ${penaltyLabel}.`;
  };

  const shouldShowLateButton =
    !isReschedulingPastInterview || (isReschedulingPastInterview && isReschedulingForSameDay);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            size="large"
            isLoading={isLoading}
            onClick={handleProceedWithReschedule}
          >
            Reschedule
          </LoadingButton>
          {shouldShowLateButton && (
            <TextButton
              LinkComponent={Link}
              size="large"
              sx={{ mt: 1, mb: 3 }}
              disabled={isReportingLateness}
              onClick={handleReportLateness}
            >
              I&apos;m just running late
            </TextButton>
          )}
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={handleCloseModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        <Illustration type="calendar-alert" />
        <Text variant="h2" textAlign="center">
          Reschedule your {rescheduleLabel}?
        </Text>
        <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
          {getDescription()}
        </Text>
      </Stack>
    </BottomSheet>
  );
}
